import React, { useEffect, useState } from "react";
import uploadIcon from "../../assets/images/upload-icon.svg";
import { Form, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import {
  BASIC_DETAIL_FIELDS,
  basicDetailsRules,
} from "../../utils/basicDetailsValidation";
import Footer from "../common/Footer";
import { useDispatch, useSelector } from "react-redux";
import { tostS, tostW } from "../../config/tostConfig";
import {
  RegisterStepOne,
  RegisterStepThree,
  RegisterStepTwo,
} from "../../api/Register";
import { setProcessData, setStepData } from "../../redux/actions/processAction";
import { useLocation } from "react-router-dom";
import SaveAsDraftModal from "../common/SaveAsDraftModal";
import {
  uploadFileService,
  validateMemberDetailService,
} from "../../api/common";
import moment from "moment";
import { truncateFilename } from "../../utils/commonFunction";

export default function ReferenceDetailsForm({
  handleBack = () => {},
  handleNext = () => {},
  handleTab = () => {},
  invalidForm = false,
  setInValidForm = () => {},
}) {
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const { data, step, currentMembership } = useSelector(
    (state) => state.process
  );
  const [loading, setLoading] = useState("");
  const [draftSaved, setDraftSaved] = useState(false);
  const [draftError, setDraftError] = useState(false);

  const validationSchema = Yup.object().shape({
    ...basicDetailsRules(currentMembership),
    director: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string()
            .nullable()
            .test("name-required-if-any", "Name is required", function (value) {
              const { email, mobile_number } = this.parent; // Access sibling fields
              return (
                (!email && !mobile_number) ||
                ((!!email || !!mobile_number) && !!value)
              ); // Name is required if email or mobile number exists
            }),
          email: Yup.string()
            .nullable()
            .email("Please enter a valid email")
            .test(
              "email-required-if-name",
              "Email is required",
              function (value) {
                const { name } = this.parent; // Access sibling fields
                return !name || (name && value); // Email is required if name exists
              }
            ),
          mobile_number: Yup.string()
            .nullable()
            .min(6, "Please enter a valid mobile number")
            .test(
              "mobile-required-if-name",
              "Mobile number is required",
              function (value) {
                const { name } = this.parent; // Access sibling fields
                return !name || (name && value); // Mobile number is required if name exists
              }
            ),
        })
      )
      .test(
        "unique-email",
        "Email addresses must be unique",
        function (values) {
          if (!values || !values.length) return true;
          const emails = values.map((item) => item.email).filter(Boolean);
          const uniqueEmails = new Set(emails);
          return emails.length === uniqueEmails.size;
        }
      )
      .test(
        "unique-mobile-number",
        "Mobile numbers must be unique",
        function (values) {
          if (!values || !values.length) return true;
          const mobileNumbers = values
            .map((item) => item.mobile_number)
            .filter(Boolean);
          const uniqueMobileNumbers = new Set(mobileNumbers);
          return mobileNumbers.length === uniqueMobileNumbers.size;
        }
      )
      .test(
        "not-users-email",
        "You can't use your email as director's Email address",
        function (values) {
          if (!values || !values.length) return true;
          const emails = values.map((item) => item.email).filter(Boolean);
          return !emails.includes(data.email);
        }
      )
      .test(
        "not-users-mobile-number",
        "You can't use your mobile number as director's mobile number",
        function (values) {
          if (!values || !values.length) return true;
          const mobileNumbers = values
            .map((item) => item.mobile_number)
            .filter(Boolean);
          return !mobileNumbers.includes(data.mobile_number);
        }
      ),
    member_name_1: Yup.string().required("Name is required"),
    member_name_2: Yup.string().required("Name is required"),
    member_director_1: Yup.string().required("Director Name is required"),
    member_director_2: Yup.string().required("Director Name is required"),
    member_email_1: Yup.string()
      .required("Email is required")
      .email("Please enter a valid email")
      .test(
        "unique-email-1",
        "You can't use your email for eema members",
        function (value) {
          return value !== data.email; // Compare with user's email
        }
      )
      .test(
        "unique-between-emails",
        "Emails must be different",
        function (value) {
          return value !== this.parent.member_email_2; // Compare with member_email_2
        }
      ),
    member_email_2: Yup.string()
      .required("Email is required")
      .email("Please enter a valid email")
      .test(
        "unique-email-2",
        "You can't use your email for eema members",
        function (value) {
          return value !== data.email; // Compare with user's email
        }
      )
      .test(
        "unique-between-emails",
        "Emails must be different",
        function (value) {
          return value !== this.parent.member_email_1; // Compare with member_email_1
        }
      ),
    member_mobile_1: Yup.string()
      .required("Mobile number is required")
      .min(6, "Please enter a valid mobile number")
      .max(15, "Please enter a valid mobile number")
      .test(
        "unique-mobile-1",
        "You can't use your mobile for eema members",
        function (value) {
          return value !== data.mobile_number; // Compare with user's mobile
        }
      )
      .test(
        "unique-between-mobiles",
        "Mobile numbers must be different",
        function (value) {
          return value !== this.parent.member_mobile_2; // Compare with member_mobile_2
        }
      ),
    member_mobile_2: Yup.string()
      .required("Mobile number is required")
      .min(6, "Please enter a valid mobile number")
      .max(15, "Please enter a valid mobile number")
      .test(
        "unique-mobile-2",
        "You can't use your mobile for eema members",
        function (value) {
          return value !== data.mobile_number; // Compare with user's mobile
        }
      )
      .test(
        "unique-between-mobiles",
        "Mobile numbers must be different",
        function (value) {
          return value !== this.parent.member_mobile_1; // Compare with member_mobile_1
        }
      ),
    signature_url: Yup.string().when([], {
      is: () =>
        currentMembership && currentMembership?.name.toLowerCase() !== "star",
      then: () => Yup.string().required("Signature is required"),
      otherwise: () => Yup.string().notRequired(),
    }),
    stamp_url: Yup.string()
      .when([], {
        is: () =>
          currentMembership && currentMembership?.name.toLowerCase() !== "star",
        then: () => Yup.string().required("Stamp is required"),
        otherwise: () => Yup.string().notRequired(),
      })
      .required("Stamp is required"),
  });

  const formik = useFormik({
    // enableReinitialize: true,
    initialValues: {
      name: data.name ? data.name : "",
      email: data.email ? data.email : "",
      dob: data.dob ? data.dob : "",
      mobile_number: data.mobile_number ? data.mobile_number : "",
      state: data.state ? data.state : "",
      zone: data.zone ? data.zone : "",
      gst_number: data.gst_number ? data.gst_number : "",
      gst_proof: data.gst_proof ? data.gst_proof : "",
      aadhar_proof: data.aadhar_proof ? data.aadhar_proof : "",
      pan_proof: data.pan_proof ? data.pan_proof : "",
      pan_number: data.pan_number ? data.pan_number : "",
      org_type: data.org_type ? data.org_type : "",
      company_name: data.company_name ? data.company_name : "",
      registered_address: data.registered_address
        ? data.registered_address
        : "",
      incorporation_date: data.incorporation_date
        ? data.incorporation_date
        : "",
      size_of_company: data.size_of_company ? data.size_of_company : "",
      primary_number: data.primary_number ? data.primary_number : "",
      same_number: data.same_number ? data.same_number : 0,
      moa: data.company && data.company.moa ? data.company.moa : "",
      aoa: data.company && data.company.aoa ? data.company.aoa : "",
      portaldirector:
        data.company && data.company.portaldirector
          ? data.company.portaldirector
          : "",
      resolution:
        data.company && data.company.resolution ? data.company.resolution : "",
      incorporation_certificate: data.incorporation_certificate
        ? data.incorporation_certificate
        : "",
      repersentative_photo: data.repersentative_photo
        ? data.repersentative_photo
        : "",
      deed: data.llp && data.llp.deed ? data.llp.deed : "",
      authorization:
        data.llp && data.llp.authorization ? data.llp.authorization : "",
      huf_deed: data.huf && data.huf.deed ? data.huf.deed : "",
      huf_authorization:
        data.huf && data.huf.authorization ? data.huf.authorization : "",
      address_proof: data.address_proof ? data.address_proof : "",
      turn_over_amount: data.turn_over_amount ? data.turn_over_amount : "",
      turn_over_year: data.turn_over_year ? data.turn_over_year : "",
      turn_over_amount_2: data.turn_over_amount_2 ? data.turn_over_amount_2 : "",
      turn_over_year_2: data.turn_over_year_2 ? data.turn_over_year_2 : "",
      turnover_certificate: data.turnover_certificate
        ? data.turnover_certificate
        : "",
      director: data.director
        ? typeof data.director === "string"
          ? JSON.parse(data.director)
          : data.director
        : "",
      member_name_1:
        data.members && data.members.length ? data.members[0].name : "",
      member_name_2:
        data.members && data.members.length ? data.members[1].name : "",
      member_email_1:
        data.members && data.members.length ? data.members[0].email : "",
      member_email_2:
        data.members && data.members.length ? data.members[1].email : "",
      member_mobile_1:
        data.members && data.members.length
          ? data.members[0].mobile_number
          : "",
      member_mobile_2:
        data.members && data.members.length
          ? data.members[1].mobile_number
          : "",
      member_director_1:
        data.members && data.members.length ? data.members[0].director : "",
      member_director_2:
        data.members && data.members.length ? data.members[1].director : "",
      signature: data.signature ? data.signature : "",
      signature_url: data.signature ? data.signature : "",
      stamp: data.stamp ? data.stamp : "",
      stamp_url: data.stamp ? data.stamp : "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading("submit");
      try {
        const isMember1Exists = await handleValidateMemberDetail(
          values.member_email_1,
          values.member_mobile_1
        );
        if (!isMember1Exists) {
          tostW("Member 1 Mobile/Email are not associate with any eema member");
          setLoading("");
          return;
        }
        const isMember2Exists = await handleValidateMemberDetail(
          values.member_email_2,
          values.member_mobile_2
        );
        if (!isMember2Exists) {
          tostW("Member 2 Mobile/Email are not associate with any eema member");
          setLoading("");
          return;
        }
      } catch (error) {
        console.log(error);
        tostW("Unable to verify member details");
        setLoading("");
        return;
      }

      const members = [
        {
          row_id: 1,
          name: values.member_name_1,
          email: values.member_email_1,
          mobile_number: values.member_mobile_1,
          director: values.member_director_1,
        },
        {
          row_id: 2,
          name: values.member_name_2,
          email: values.member_email_2,
          mobile_number: values.member_mobile_2,
          director: values.member_director_2,
        },
      ];
      const formData = new FormData();
      formData.set("id", data.id);
      if (!pathname.includes("upgrade")) {
        formData.set("steps", 3);
      }
      formData.set("members", JSON.stringify(members));
      if (typeof values.signature === "string") {
        formData.set("old_signature", values.signature);
      } else {
        formData.set("signature", values.signature);
      }
      if (typeof values.stamp === "string") {
        formData.set("old_stamp", values.stamp);
      } else {
        formData.set("stamp", values.stamp);
      }
      formData.set("is_draft", 0);
      formData.set("journey", pathname.includes("upgrade") ? "upgrade" : "new");
      if (data.app_id) {
        formData.set("app_id", data.app_id);
      }

      try {
        if (step < 3) {
          if (step < 2) {
            let formData1 = new FormData();

            formData1.set("id", data.id);
            formData1.set("name", data.name);
            formData1.set("email", data.email);
            formData1.set("dob", moment(data.dob).format("YYYY-MM-DD"));
            formData1.set("mobile_number", data.mobile_number);
            formData1.set("pan_number", data.pan_number);
            formData1.set(
              "gst_number",
              data.gst_number &&
                data.gst_number !== null &&
                data.gst_number !== "null"
                ? data.gst_number
                : ""
            );
            formData1.set("zone", data.zone);
            formData1.set("state", data.state);
            formData1.set(
              "membership_id",
              currentMembership
                ? currentMembership.id
                : data.plan_history.plan_id
            );
            formData1.set("company_name", data.company_name);
            formData1.set("registered_address", data.registered_address);
            formData1.set("size_of_company", data.size_of_company);
            formData1.set("primary_number", data.primary_number);
            formData1.set("same_number", data.same_number);
            formData1.set("repersentative_photo", data.repersentative_photo);
            formData1.set("address_proof", data.address_proof);
            formData1.set("journey", "upgrade");
            formData1.set("is_draft", 0);

            formData1.set("aadhar_proof", data.aadhar_proof);
            formData1.set("pan_proof", data.pan_proof);

            if (
              currentMembership &&
              currentMembership.name.toLowerCase() !== "star"
            ) {
              formData1.set("gst_proof", data.gst_proof);
              formData1.set("turn_over_amount", data.turn_over_amount);
              formData1.set("turn_over_year", data.turn_over_year);
              formData1.set("turn_over_amount_2", data.turn_over_amount_2);
              formData1.set("turn_over_year_2", data.turn_over_year_2);
              if (data.org_type) {
                formData1.set("org_type", data.org_type);
                formData1.set(
                  "incorporation_date",
                  moment(data.incorporation_date).format("YYYY-MM-DD")
                );
                formData1.set(
                  "turnover_certificate",
                  data.turnover_certificate
                );
              }

              if (data.org_type && data.org_type.toLowerCase() === "company") {
                formData1.set("moa", data.company.moa);
                formData1.set("aoa", data.company.aoa);
                formData1.set("portaldirector", data.company.portaldirector);
                formData1.set("resolution", data.company.resolution);
                formData1.set(
                  "incorporation_certificate",
                  data.incorporation_certificate
                );
              }
              if (
                data.org_type &&
                data.org_type.toLowerCase() === "partnership"
              ) {
                formData1.set("deed", data.llp.deed);
                formData1.set("authorization", data.llp.authorization);
              }
              if (data.org_type && data.org_type.toLowerCase() === "huf") {
                formData1.set("deed", data.huf.deed);
                formData1.set("authorization", data.huf.authorization);
              }
            }
            formData1.set(
              "journey",
              pathname.includes("upgrade") ? "upgrade" : "new"
            );
            const directorBody = {
              id: data.id,
              director:
                currentMembership &&
                currentMembership.name.toLowerCase() !== "star"
                  ? data.director
                  : null,
              is_draft: 0,
              journey: pathname.includes("upgrade") ? "upgrade" : "new",
            };

            if (data.app_id) {
              directorBody.app_id = data.app_id;
            }

            const res1 = await RegisterStepOne(formData1);
            if (res1.status === 200 && res1.data.success) {
              dispatch(
                setProcessData({ id: res1.data.data, app_id: res1.data.app_id })
              );
              directorBody.app_id = res1.data.app_id;
              const res2 = await RegisterStepTwo(directorBody);
              if (res2.status === 200 && res2.data.success) {
                dispatch(setProcessData({ ...res2.data.data }));
                formData.set("app_id", res1.data.app_id);
                const res3 = await RegisterStepThree(formData);
                if (res3.status === 200 && res3.data.success) {
                  tostS(res3.data.message);
                  dispatch(setProcessData({ ...res3.data.data }));
                  dispatch(setStepData(4));
                  handleNext();
                } else {
                  tostW(res3.data.message);
                }
              }
            }
          } else {
            const directorBody = {
              id: data.id,
              director:
                currentMembership &&
                currentMembership.name.toLowerCase() !== "star"
                  ? data.director?.filter(
                      (item) => item.name && item.email && item.mobile_number
                    ).length
                    ? data.director
                    : null
                  : null,
            };
            if (data.app_id) {
              directorBody.app_id = data.app_id;
            }
            const res2 = await RegisterStepTwo(directorBody);
            if (res2.status === 200 && res2.data.success) {
              dispatch(setProcessData({ ...res2.data.data }));
              const res3 = await RegisterStepThree(formData);
              if (res3.status === 200 && res3.data.success) {
                tostS(res3.data.message);
                dispatch(setProcessData({ ...res3.data.data }));
                dispatch(setStepData(4));
                handleNext();
              } else {
                tostW(res3.data.message);
              }
            }
          }
        } else {
          const res = await RegisterStepThree(formData);
          if (res.status === 200 && res.data.success) {
            tostS(res.data.message);
            dispatch(setProcessData({ ...res.data.data }));
            dispatch(setStepData(4));
            handleNext();
          } else {
            tostW(res.data.message);
          }
        }
      } catch (error) {
        tostW("Something went wrong");
        console.log(error);
      } finally {
        setLoading("");
      }
    },
  });

  const {
    values,
    errors,
    touched,
    setErrors,
    setTouched,
    handleSubmit,
    setFieldValue,
    validateForm,
  } = formik;

  useEffect(() => {
    if (invalidForm) {
      validateForm().then((validationErrors) => {
        if (Object.keys(validationErrors).length > 0) {
          setErrors(validationErrors);
          setTouched(
            Object.keys(validationErrors).reduce(
              (acc, key) => ({ ...acc, [key]: true }),
              {}
            )
          );
        }
      });
      setInValidForm(false);
    }
  }, [validateForm]); // eslint-disable-line

  const handleFileUpload = async (e) => {
    const fieldName = e.target.name;
    const file = e.target.files[0];
    if (!file) return;
    // Validate file type
    const validFileTypes = ["image/jpeg", "image/png", "image/jpg"];
    if (!validFileTypes.includes(file.type)) {
      tostW("Only JPEG, PNG, and JPG files are allowed.");
      return;
    }

    // Validate file size (5 MB = 5 * 1024 * 1024 bytes)
    const maxSizeInBytes = 5 * 1024 * 1024;
    if (file.size > maxSizeInBytes) {
      tostW("File size must be less than 5 MB.");
      return;
    }
    const body = new FormData();
    body.append("file", file);
    if (values[fieldName] !== "") {
      body.append("old_file_name", values[fieldName].split(".com").pop());
    }

    try {
      const fileResponse = await uploadFileService(body);
      if (fileResponse.status === 200 && fileResponse.data.success) {
        const fileUrl = fileResponse.data.url;
        setFieldValue(fieldName, fileUrl);
        setFieldValue(`${fieldName}_url`, fileUrl);
        dispatch(setProcessData({ ...data, [fieldName]: fileUrl }));
      }
    } catch (error) {
      console.log(error);
      tostW("Error uploading file");
    }
  };

  const handleSaveAsDraft = async () => {
    if (loading === "save_draft" || loading === "submit") return;
    setLoading("save_draft");
    const members = [
      {
        row_id: 1,
        name: values.member_name_1,
        email: values.member_email_1,
        mobile_number: values.member_mobile_1,
        director: values.member_director_1,
      },
      {
        row_id: 2,
        name: values.member_name_2,
        email: values.member_email_2,
        mobile_number: values.member_mobile_2,
        director: values.member_director_2,
      },
    ];
    if (!data.id) {
      tostW("Please submit basic details first");
      handleTab(1);
      return;
    }
    const formData = new FormData();
    formData.set("id", data.id);
    if (!pathname.includes("upgrade")) {
      formData.set("steps", 2);
    }
    formData.set("members", JSON.stringify(members));
    if (typeof values.signature === "string") {
      formData.set("old_signature", values.signature);
    } else {
      formData.set("signature", values.signature);
    }
    if (typeof values.stamp === "string") {
      formData.set("old_stamp", values.stamp);
    } else {
      formData.set("stamp", values.stamp);
    }
    formData.set("membership_checked", values.membership_checked ? 1 : 0);
    formData.set("is_draft", 1);
    formData.set("journey", pathname.includes("upgrade") ? "upgrade" : "new");

    if (data.app_id) {
      formData.set("app_id", data.app_id);
    }

    try {
      const res = await RegisterStepThree(formData);
      if (res.status === 200 && res.data.success) {
        setDraftSaved(true);
        dispatch(setProcessData({ ...res.data.data }));
      } else {
        setDraftError(true);
      }
    } catch (error) {
      setDraftError(true);
      console.log(error);
    } finally {
      setLoading("");
    }
  };

  const handleValidateMemberDetail = async (email, phone) => {
    try {
      const params = {
        email,
        phone,
      };
      const res = await validateMemberDetailService(params);
      if (res.status === 200 && res.data.success) {
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.log(error);
      return false;
    }
  };

  return (
    <>
      <FormikProvider value={formik}>
        <Form onSubmit={handleSubmit}>
          <div className="membershipForm">
            <h4>PROPOSED BY (EEMA Members 1)</h4>
            <div className="row">
              <div className="col-md-6 col-lg-6 form-group">
                <input
                  className="form-control"
                  placeholder="Name of Company"
                  name="member_name_1"
                  value={values.member_name_1}
                  onChange={(e) => {
                    setFieldValue("member_name_1", e.target.value);
                    const newData = {
                      ...data,
                      members:
                        data.members && data.members.length
                          ? [...data.members]
                          : [{}, {}], // Ensure members array exists
                    };
                    newData.members[0] = {
                      ...newData.members[0],
                      name: e.target.value,
                    };
                    dispatch(setProcessData(newData));
                  }}
                />
                {touched.member_name_1 && errors.member_name_1 && (
                  <p className="error">{errors.member_name_1}</p>
                )}
              </div>
              <div className="col-md-6 col-lg-6 form-group">
                <input
                  className="form-control"
                  placeholder="Name of Director"
                  name="member_director_1"
                  value={values.member_director_1}
                  onChange={(e) => {
                    setFieldValue("member_director_1", e.target.value);
                    const newData = {
                      ...data,
                      members:
                        data.members && data.members.length
                          ? [...data.members]
                          : [{}, {}], // Ensure members array exists
                    };
                    newData.members[0] = {
                      ...newData.members[0],
                      director: e.target.value,
                    };
                    dispatch(setProcessData(newData));
                  }}
                />
                {touched.member_director_1 && errors.member_director_1 && (
                  <p className="error">{errors.member_director_1}</p>
                )}
              </div>
              <div className="col-md-6 col-lg-6 form-group">
                <input
                  className="form-control"
                  placeholder="Email Address"
                  name="member_email_1"
                  value={values.member_email_1}
                  onChange={(e) => {
                    setFieldValue("member_email_1", e.target.value);
                    const newData = {
                      ...data,
                      members:
                        data.members && data.members.length
                          ? [...data.members]
                          : [{}, {}], // Ensure members array exists
                    };
                    newData.members[0] = {
                      ...newData.members[0],
                      email: e.target.value,
                    };
                    dispatch(setProcessData(newData));
                  }}
                />
                {touched.member_email_1 && errors.member_email_1 && (
                  <p className="error">{errors.member_email_1}</p>
                )}
              </div>
              <div className="col-md-6 col-lg-6 form-group">
                <input
                  className="form-control"
                  placeholder="Mobile Number"
                  name="member_mobile_1"
                  value={values.member_mobile_1}
                  onChange={(e) => {
                    if (e.target.value.toString().length > 15) {
                      return;
                    }
                    setFieldValue("member_mobile_1", e.target.value);
                    const newData = {
                      ...data,
                      members:
                        data.members && data.members.length
                          ? [...data.members]
                          : [{}, {}], // Ensure members array exists
                    };
                    newData.members[0] = {
                      ...newData.members[0],
                      mobile_number: e.target.value,
                    };
                    dispatch(setProcessData(newData));
                  }}
                />
                {touched.member_mobile_1 && errors.member_mobile_1 && (
                  <p className="error">{errors.member_mobile_1}</p>
                )}
              </div>
            </div>
            <h4>PROPOSED BY (EEMA Members 2)</h4>
            <div className="row">
              <div className="col-md-6 col-lg-6 form-group">
                <input
                  className="form-control"
                  placeholder="Name of Company"
                  name="member_name_2"
                  value={values.member_name_2}
                  onChange={(e) => {
                    setFieldValue("member_name_2", e.target.value);
                    const newData = {
                      ...data,
                      members:
                        data.members && data.members.length
                          ? [...data.members]
                          : [{}, {}],
                    };
                    newData.members[1] = {
                      ...newData.members[1],
                      name: e.target.value,
                    };
                    dispatch(setProcessData(newData));
                  }}
                />
                {touched.member_name_2 && errors.member_name_2 && (
                  <p className="error">{errors.member_name_2}</p>
                )}
              </div>
              <div className="col-md-6 col-lg-6 form-group">
                <input
                  className="form-control"
                  placeholder="Name of Director"
                  name="member_director_2"
                  value={values.member_director_2}
                  onChange={(e) => {
                    setFieldValue("member_director_2", e.target.value);
                    const newData = {
                      ...data,
                      members:
                        data.members && data.members.length
                          ? [...data.members]
                          : [{}, {}],
                    };
                    newData.members[1] = {
                      ...newData.members[1],
                      director: e.target.value,
                    };
                    dispatch(setProcessData(newData));
                  }}
                />
                {touched.member_director_2 && errors.member_director_2 && (
                  <p className="error">{errors.member_director_2}</p>
                )}
              </div>
              <div className="col-md-6 col-lg-6 form-group">
                <input
                  className="form-control"
                  placeholder="Email Address"
                  name="member_email_2"
                  value={values.member_email_2}
                  onChange={(e) => {
                    setFieldValue("member_email_2", e.target.value);
                    const newData = {
                      ...data,
                      members:
                        data.members && data.members.length
                          ? [...data.members]
                          : [{}, {}],
                    };
                    newData.members[1] = {
                      ...newData.members[1],
                      email: e.target.value,
                    };
                    dispatch(setProcessData(newData));
                  }}
                />
                {touched.member_email_2 && errors.member_email_2 && (
                  <p className="error">{errors.member_email_2}</p>
                )}
              </div>
              <div className="col-md-6 col-lg-6 form-group">
                <input
                  className="form-control"
                  placeholder="Mobile Number"
                  name="member_mobile_2"
                  value={values.member_mobile_2}
                  onChange={(e) => {
                    if (e.target.value.toString().length > 15) {
                      return;
                    }
                    setFieldValue("member_mobile_2", e.target.value);
                    const newData = {
                      ...data,
                      members:
                        data.members && data.members.length
                          ? [...data.members]
                          : [{}, {}],
                    };
                    newData.members[1] = {
                      ...newData.members[1],
                      mobile_number: e.target.value,
                    };
                    dispatch(setProcessData(newData));
                  }}
                />
                {touched.member_mobile_2 && errors.member_mobile_2 && (
                  <p className="error">{errors.member_mobile_2}</p>
                )}
              </div>
            </div>
            {currentMembership &&
              currentMembership?.name.toLowerCase() !== "star" && (
                <>
                  <h4>COMPANY AUTHORIZED SIGNATURE & STAMP</h4>
                  <div className="row">
                    <div className="col-md-6 ">
                      <div className="form-group">
                        <div className="customFile">
                          <input
                            title={
                              values.signature
                                ? values.signature.name ||
                                  values.signature_url.split("/").pop()
                                : "Drag & Drop your file here"
                            }
                            onChange={(e) => handleFileUpload(e)}
                            name="signature"
                            className="form-control"
                            type="file"
                            accept="image/*"
                          />
                          <img src={uploadIcon} alt="" />
                          <h4>Signature</h4>
                          <small className="mb-2">
                            Allowed formats: .jpg, .jpeg, .png (Max size: 5MB)
                          </small>
                          <p>
                            {values.signature
                              ? truncateFilename(
                                  values.signature.name ||
                                    values.signature_url.split("/").pop(),
                                  15,
                                  15
                                )
                              : (
                                <>
                                  Drag & Drop your file here <br />
                                  Signature & Stamp of the person applying for the Membership
                                </>
                              )}
                          </p>
                        </div>
                        {touched.signature_url && errors.signature_url && (
                          <p className="error">{errors.signature_url}</p>
                        )}
                      </div>
                    </div>
                    <div className="col-md-6 ">
                      <div className="form-group">
                        <div className="customFile">
                          <input
                            title={
                              values.stamp
                                ? values.stamp.name ||
                                  values.stamp_url.split("/").pop()
                                : "Drag & Drop your file here"
                            }
                            onChange={(e) => handleFileUpload(e)}
                            name="stamp"
                            className="form-control"
                            type="file"
                            accept="image/*"
                          />
                          <img src={uploadIcon} alt="" />
                          <h4>Stamp</h4>
                          <small className="mb-2">
                            Allowed formats: .jpg, .jpeg, .png (Max size: 5MB)
                          </small>
                          <p>
                            {values.stamp
                              ? truncateFilename(
                                  values.stamp.name ||
                                    values.stamp_url.split("/").pop(),
                                  15,
                                  15
                                )
                              : "Drag & Drop your file here"}
                          </p>
                        </div>
                        {touched.stamp_url && errors.stamp_url && (
                          <p className="error">{errors.stamp_url}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </>
              )}
          </div>
        </Form>
      </FormikProvider>
      <Footer
        isBackAllowed
        isNextAllowed={loading !== "submit"}
        isSaveAsDraft
        disableSaveAsDraft={false}
        backButtonAction={() => handleBack()}
        nextButtonText="Next"
        nextButtonAction={() => {
          if (!data.id) {
            tostW("Please submit basic details first!!");
            handleTab(1);
            return;
          }
          formik.validateForm().then((errors) => {
            const invalidBasicDetail = Object.keys(errors).filter((key) =>
              BASIC_DETAIL_FIELDS.includes(key)
            );
            if (invalidBasicDetail.length > 0) {
              tostW("Please complete this form accurately.");
              handleTab(1);
              setInValidForm(true);
              return;
            }
            if (Object.keys(errors).includes("director")) {
              tostW("Please complete this form accurately.");
              handleTab(2);
              setInValidForm(true);
              return;
            }
          });
          handleSubmit();
        }}
        saveAsDraftAction={() => handleSaveAsDraft()}
      />
      {(draftSaved || draftError) && (
        <SaveAsDraftModal
          handleCloseModal={() => {
            setDraftSaved(false);
            setDraftError(false);
          }}
          error={draftError}
        />
      )}
    </>
  );
}
